import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    Grid,
    Card,
    CardContent,
    Divider
} from '@mui/material';
import { AccountTree as AccountTreeIcon, Link as LinkIcon, Storage as StorageIcon } from '@mui/icons-material';
import toastr from 'toastr';
import * as LegacyViewerService from '../../../services/clinicPortalServices/legacyViewerService';

const DataRelationships = () => {
    const [loading, setLoading] = useState(true);
    const [relationships, setRelationships] = useState([]);
    const [groupedRelationships, setGroupedRelationships] = useState({});

    useEffect(() => {
        loadRelationships();
    }, []);

    const loadRelationships = async () => {
        try {
            setLoading(true);
            const response = await LegacyViewerService.getEntityRelationships();

            if (response.success) {
                setRelationships(response.data);

                // Group relationships by source entity
                const grouped = response.data.reduce((acc, rel) => {
                    if (!acc[rel.sourceEntity]) {
                        acc[rel.sourceEntity] = [];
                    }
                    acc[rel.sourceEntity].push(rel);
                    return acc;
                }, {});

                setGroupedRelationships(grouped);
            }
        } catch (error) {
            toastr.error('Failed to load relationships');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getRelationshipColor = (targetEntity) => {
        const colors = {
            Patient: 'primary',
            Facility: 'secondary',
            Physician: 'success',
            Test: 'info',
            Requisition: 'warning',
            Sample: 'error'
        };
        return colors[targetEntity] || 'default';
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (relationships.length === 0) {
        return <Alert severity="info">No relationships found. Please scan files first to detect relationships.</Alert>;
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Entity Relationships Map
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Discovered {relationships.length} relationships across {Object.keys(groupedRelationships).length}{' '}
                entities
            </Typography>

            <Grid container spacing={3}>
                {Object.entries(groupedRelationships).map(([sourceEntity, relations]) => (
                    <Grid item xs={12} md={6} lg={4} key={sourceEntity}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <StorageIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">{sourceEntity.replace(/_/g, ' ')}</Typography>
                                </Box>

                                <Divider sx={{ mb: 2 }} />

                                <List dense>
                                    {relations.map((rel, index) => (
                                        <ListItem key={index}>
                                            <ListItemIcon>
                                                <LinkIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Typography variant="body2">{rel.sourceField}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            →
                                                        </Typography>
                                                        <Chip
                                                            label={rel.targetEntity}
                                                            size="small"
                                                            color={getRelationshipColor(rel.targetEntity)}
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Typography variant="caption" color="text.secondary">
                                                        Pattern: {rel.pattern}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Paper sx={{ mt: 3, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Relationship Summary
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            Most Connected Entities
                        </Typography>
                        <List dense>
                            {Object.entries(groupedRelationships)
                                .sort((a, b) => b[1].length - a[1].length)
                                .slice(0, 5)
                                .map(([entity, rels]) => (
                                    <ListItem key={entity}>
                                        <ListItemText
                                            primary={entity.replace(/_/g, ' ')}
                                            secondary={`${rels.length} relationships`}
                                        />
                                    </ListItem>
                                ))}
                        </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            Common Target Entities
                        </Typography>
                        <List dense>
                            {Object.entries(
                                relationships.reduce((acc, rel) => {
                                    acc[rel.targetEntity] = (acc[rel.targetEntity] || 0) + 1;
                                    return acc;
                                }, {})
                            )
                                .sort((a, b) => b[1] - a[1])
                                .slice(0, 5)
                                .map(([entity, count]) => (
                                    <ListItem key={entity}>
                                        <ListItemText primary={entity} secondary={`Referenced ${count} times`} />
                                    </ListItem>
                                ))}
                        </List>
                    </Grid>
                </Grid>
            </Paper>

            <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                    These relationships are automatically detected based on field naming patterns and data formats.
                    Foreign key format detected: <code>EntityType;ID1;ID2;...</code>
                </Typography>
            </Alert>
        </Box>
    );
};

export default DataRelationships;
